"use client";

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Database, Globe } from 'lucide-react';

interface EnvironmentIndicatorProps {
  className?: string;
  variant?: 'default' | 'compact';
  theme?: 'light' | 'dark';
}

export function EnvironmentIndicator({ 
  className = '', 
  variant = 'default',
  theme = 'light'
}: EnvironmentIndicatorProps) {
  const envName = process.env.NEXT_PUBLIC_ENV_NAME || 'Unknown';
  const dbName = process.env.NEXT_PUBLIC_DB_NAME || 'Unknown';

  // Determine environment colors based on theme
  const getEnvironmentColor = (env: string, isDark: boolean) => {
    if (isDark) {
      switch (env.toLowerCase()) {
        case 'production':
          return 'bg-green-600 text-white border-green-500';
        case 'staging':
          return 'bg-yellow-600 text-white border-yellow-500';
        case 'local':
        case 'development':
          return 'bg-blue-600 text-white border-blue-500';
        default:
          return 'bg-gray-600 text-white border-gray-500';
      }
    } else {
      switch (env.toLowerCase()) {
        case 'production':
          return 'bg-green-100 text-green-800 border-green-300';
        case 'staging':
          return 'bg-yellow-100 text-yellow-800 border-yellow-300';
        case 'local':
        case 'development':
          return 'bg-blue-100 text-blue-800 border-blue-300';
        default:
          return 'bg-gray-100 text-gray-800 border-gray-300';
      }
    }
  };

  const getDatabaseColor = (isDark: boolean) => {
    return isDark 
      ? 'bg-gray-600 text-white border-gray-500'
      : 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const environmentColor = getEnvironmentColor(envName, theme === 'dark');
  const databaseColor = getDatabaseColor(theme === 'dark');

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Badge 
          variant="outline" 
          className={`text-xs font-medium ${environmentColor}`}
        >
          <Globe className="w-3 h-3 mr-1" />
          {envName}
        </Badge>
        <Badge 
          variant="outline" 
          className={`text-xs font-medium ${databaseColor}`}
        >
          <Database className="w-3 h-3 mr-1" />
          {dbName}
        </Badge>
      </div>
    );
  }

  const iconColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const textColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex items-center gap-2">
        <Globe className={`w-4 h-4 ${iconColor}`} />
        <span className={`text-sm ${textColor}`}>Environment:</span>
        <Badge 
          variant="outline" 
          className={`text-sm font-medium ${environmentColor}`}
        >
          {envName}
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        <Database className={`w-4 h-4 ${iconColor}`} />
        <span className={`text-sm ${textColor}`}>Database:</span>
        <Badge 
          variant="outline" 
          className={`text-sm font-medium ${databaseColor}`}
        >
          {dbName}
        </Badge>
      </div>
    </div>
  );
}
